import {
  apiV1Auth2FaBackupCodesPost,
  changePasswordApiV1AuthChangePasswordPost,
  disable2FaApiV1Auth2FaDisablePost,
  enable2FaApiV1Auth2FaEnablePost,
  loginApiV1AuthLoginPost,
  logoutApiV1AuthLogoutPost,
  registerApiV1AuthRegisterPost,
  setup2FaApiV1Auth2FaSetupPost,
  updateCheckoutDataApiV1AuthCheckoutDataPost,
  verify2FaApiV1Auth2FaVerifyPost,
} from "@/backend-sdk/client/sdk.gen";
import type {
  BackupCodesResponse,
  ChangePasswordRequest,
  CheckoutDataRequest,
  CheckoutDataResponse,
  Disable2FaRequest,
  LoginRequest,
  RegisterRequest as BackendRegisterRequest,
  RegisterResponse,
  Setup2FaResponse,
  TokenResponse,
} from "@/backend-sdk/client/types.gen";
import {
  clearHeyApiSubAccountToken,
  clearHeyApiTokens,
  setHeyApiTokens,
} from "@/lib/hey-api-client";
import { INTEGRATIONS_WELCOME_PENDING_USER_ID_KEY } from "@/lib/onboarding";

const authRequestHeaders = {
  headers: {
    "x-ignore-global-filters": "1",
  },
};

type Setup2FAResponse = Setup2FaResponse;
type Disable2FARequest = Disable2FaRequest;
export type RegisterRequest = Omit<
  BackendRegisterRequest,
  "confirm_password"
> & {
  confirm_password?: string;
};

class AuthService {
  // Registrar novo usuário
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    // Adicionar confirm_password ao payload
    const payload: BackendRegisterRequest = {
      ...data,
      confirm_password: data.confirm_password ?? data.password,
    };
    const { data: registerData } = await registerApiV1AuthRegisterPost({
      body: payload,
      ...authRequestHeaders,
      throwOnError: true,
    });

    // Salvar tokens automaticamente após registro bem-sucedido
    setHeyApiTokens({
      access_token: registerData.access_token,
      refresh_token: registerData.refresh_token,
      token_type: registerData.token_type,
      expires_in: registerData.expires_in,
      needs_2fa: registerData.needs_2fa,
    });

    // Marca onboarding pendente para exibir o modal de boas-vindas apenas para novos cadastros.
    if (registerData.user?.id) {
      localStorage.setItem(
        INTEGRATIONS_WELCOME_PENDING_USER_ID_KEY,
        registerData.user.id,
      );
    }

    return registerData;
  }

  // Fazer login
  async login(data: LoginRequest): Promise<TokenResponse> {
    const { data: tokens } = await loginApiV1AuthLoginPost({
      body: data,
      ...authRequestHeaders,
      throwOnError: true,
    });

    // Sempre salvar tokens, mesmo se for temporário para 2FA
    clearHeyApiSubAccountToken();
    setHeyApiTokens(tokens);

    return tokens;
  }

  // Verificar código 2FA
  async verify2FA(code: string): Promise<TokenResponse> {
    const { data: tokens } = await verify2FaApiV1Auth2FaVerifyPost({
      body: { code },
      ...authRequestHeaders,
      throwOnError: true,
    });
    setHeyApiTokens(tokens);
    return tokens;
  }

  // Fazer logout
  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        await logoutApiV1AuthLogoutPost({
          query: { refresh_token: refreshToken },
          ...authRequestHeaders,
          throwOnError: true,
        });
      }
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    } finally {
      clearHeyApiTokens();
      clearHeyApiSubAccountToken();
      // Não fazer redirect aqui, deixar o contexto lidar com isso
    }
  }

  // Trocar senha
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await changePasswordApiV1AuthChangePasswordPost({
      body: data,
      ...authRequestHeaders,
      throwOnError: true,
    });
  }

  // Configurar 2FA
  async setup2FA(): Promise<Setup2FAResponse> {
    const { data } = await setup2FaApiV1Auth2FaSetupPost({
      ...authRequestHeaders,
      throwOnError: true,
    });
    return data;
  }

  // Ativar 2FA
  async enable2FA(code: string): Promise<void> {
    await enable2FaApiV1Auth2FaEnablePost({
      body: { code },
      ...authRequestHeaders,
      throwOnError: true,
    });
  }

  /**
   * <M-018> Imprime um lote novo de códigos de reserva, invalidando o anterior.
   *
   * Exige só a senha, e não um código do autenticador: quem pede códigos de reserva pode
   * já ter perdido o aparelho que gera o outro. É também o único caminho para quem ligou
   * o 2FA antes de os códigos passarem a valer — `setup2FA` responde `2FA already enabled`
   * a essa pessoa.
   */
  async regenerateBackupCodes(password: string): Promise<BackupCodesResponse> {
    const { data } = await apiV1Auth2FaBackupCodesPost({
      body: { password },
      ...authRequestHeaders,
      throwOnError: true,
    });
    return data;
  }

  // Desativar 2FA
  async disable2FA(data: Disable2FARequest): Promise<void> {
    await disable2FaApiV1Auth2FaDisablePost({
      body: data,
      ...authRequestHeaders,
      throwOnError: true,
    });
  }

  // Atualizar dados no checkout
  async updateCheckoutData(
    data: CheckoutDataRequest,
  ): Promise<CheckoutDataResponse> {
    const { data: response } =
      await updateCheckoutDataApiV1AuthCheckoutDataPost({
        body: data,
        ...authRequestHeaders,
        throwOnError: true,
      });
    return response;
  }

  // Verificar se está autenticado
  isAuthenticated(): boolean {
    return !!localStorage.getItem("access_token");
  }

  // Obter token de acesso
  getAccessToken(): string | null {
    return localStorage.getItem("access_token");
  }
}

export default new AuthService();
